'use client';

import { AlertTriangle, Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from './Button';
import { useTranslations } from '@/i18n/I18nProvider';

/**
 * Development environment banner - displays warning on dev.* or *.dev.* domains
 * Should be placed ABOVE the navbar
 */
export function DevBanner() {
    const [isDevEnvironment, setIsDevEnvironment] = useState(false);
    const [productionUrl, setProductionUrl] = useState('https://better-splan.hfu.digital');
    const { t } = useTranslations('banner');

    useEffect(() => {
        const hostname = window.location.hostname;
        // Detect both patterns: dev.example.com and example.dev.hfu.digital
        const isDev = hostname.startsWith('dev.') || hostname.includes('.dev.');
        setIsDevEnvironment(isDev);

        if (isDev) {
            const url = new URL(window.location.href);
            // Handle both patterns
            if (url.hostname.startsWith('dev.')) {
                url.hostname = url.hostname.replace(/^dev\./, '');
            } else {
                url.hostname = url.hostname.replace('.dev.', '.');
            }
            setProductionUrl(url.toString());
        }
    }, []);

    if (!isDevEnvironment) {
        return null;
    }

    return (
        <div className="border-b border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950">
            <div className="container mx-auto flex flex-col items-start gap-2 px-4 py-2 max-w-7xl sm:flex-row sm:items-center">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 text-red-600 dark:text-red-400" />
                <p className="flex-1 text-sm text-red-900 dark:text-red-100">
                    <strong>{t('devEnvironment')}</strong> {t('devWarning')}
                </p>
                <div className="flex gap-2">
                    <a href="https://discord.gg/hfu" target="_blank" rel="noopener noreferrer">
                        <Button
                            variant="outline"
                            size="sm"
                            className="border-red-300 bg-red-100 text-red-900 hover:bg-red-200 dark:border-red-800 dark:bg-red-900 dark:text-red-100 dark:hover:bg-red-800"
                        >
                            {t('support')}
                        </Button>
                    </a>
                    <a href={productionUrl}>
                        <Button
                            variant="outline"
                            size="sm"
                            className="border-red-300 bg-red-100 text-red-900 hover:bg-red-200 dark:border-red-800 dark:bg-red-900 dark:text-red-100 dark:hover:bg-red-800"
                        >
                            {t('goToProduction')}
                        </Button>
                    </a>
                </div>
            </div>
        </div>
    );
}

/**
 * HFU affiliation/connection law warning banner
 * Should be placed BELOW the navbar
 */
export function AffiliationBanner() {
    const [hostname, setHostname] = useState('splan.hfu.digital');
    const { t } = useTranslations('banner');

    useEffect(() => {
        setHostname(window.location.hostname);
    }, []);

    return (
        <div className="border-b border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950">
            <div className="container mx-auto flex flex-col items-start gap-2 px-4 py-2 max-w-7xl sm:flex-row sm:items-center">
                <Info className="h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="flex-1 text-sm text-amber-900 dark:text-amber-100">
                    <strong>{t('notice')}</strong> {t('affiliationWarning', { hostname })}
                </p>
                <a href="https://azb.hfu.digital/about" target="_blank" rel="noopener noreferrer">
                    <Button
                        variant="outline"
                        size="sm"
                        className="border-amber-300 bg-amber-100 text-amber-900 hover:bg-amber-200 dark:border-amber-800 dark:bg-amber-900 dark:text-amber-100 dark:hover:bg-amber-800"
                    >
                        {t('moreInfo')}
                    </Button>
                </a>
            </div>
        </div>
    );
}
